import { useI18n } from '../i18n/I18nProvider';
import { useEffect, useId, useRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { AlertCircle, ArrowUpRight, Loader2, X, Inbox, RefreshCw } from 'lucide-react';
import { initials } from '../lib/format';
import { useWorkspaceIdentity } from '../workspace/WorkspaceIdentity';

export function Button({ children, variant = 'primary', className = '', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger' }) {
  return <button className={`btn btn-${variant} ${className}`} {...props}>{children}</button>;
}
export function IconButton({ label, children, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  const { t } = useI18n();
  return <button className="icon-button" aria-label={t(label)} title={t(label)} {...props}>{children}</button>;
}
export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'green' | 'blue' | 'amber' | 'red' | 'purple' }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}
export function Avatar({ name, color = 'blue', size = '', src }: { name: string; color?: string; size?: 'small' | 'large' | ''; src?: string }) {
  return <span aria-hidden="true" className={`avatar avatar-${color} avatar-${size}`}>{src ? <img src={src} alt="" loading="lazy" /> : initials(name)}</span>;
}
export function Logo({ light = false }: { light?: boolean }) {
  const { storeName } = useWorkspaceIdentity();
  return <div className={`logo ${light ? 'logo-light' : ''}`}><span className="logo-symbol"><i /><i /></span>{storeName ? <span>{storeName}<small>OWNER WORKSPACE</small></span> : <span>STORE<span className="logo-x">·X</span><small>SALON WORKSPACE</small></span>}</div>;
}
export function EmptyState({ title = 'Chưa có dữ liệu', description = 'Dữ liệu sẽ xuất hiện tại đây khi có cập nhật.', action }: { title?: string; description?: string; action?: ReactNode }) {
  const { t } = useI18n();
  return <div className="empty-state"><span className="empty-icon"><Inbox size={26} /></span><h3>{t(title)}</h3><p>{t(description)}</p>{action}</div>;
}
export function LoadingState() {
  const { t } = useI18n();

  return <div className="loading-state" role="status"><Loader2 className="spin" size={24} /><span>{t("Đang tải dữ liệu…")}</span></div>;
}
export function ErrorState({ message, retry }: { message: string; retry?: () => void }) {
  const { t } = useI18n();

  return <div className="error-state" role="alert"><AlertCircle size={22} /><div><strong>{t("Chưa thể tải dữ liệu")}</strong><p>{t(message)}</p></div>{retry && <Button variant="secondary" onClick={retry}><RefreshCw size={15} />{t("Thử lại")}</Button>}</div>;
}
export function Modal({ title, children, onClose, wide = false, className = '' }: { title: string; children: ReactNode; onClose: () => void; wide?: boolean; className?: string }) {
  const { t } = useI18n();

  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => { const dialog = ref.current; dialog?.showModal(); return () => { dialog?.close(); }; }, []);
  return <dialog ref={ref} aria-labelledby={titleId} className={`modal ${wide ? 'modal-wide' : ''} ${className}`} onCancel={event => { event.preventDefault(); onClose(); }} onClick={event => { if (event.target === event.currentTarget) onClose(); }}><div className="modal-title"><h2 id={titleId}>{title}</h2><IconButton label={t("Đóng")} onClick={onClose}><X size={19} /></IconButton></div>{children}</dialog>;
}
export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return <div className="section-title"><h3>{children}</h3>{action}</div>;
}
export function TextLink({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return <button className="text-link" onClick={onClick}>{children}<ArrowUpRight size={14} /></button>;
}
